import React, { useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../hooks/hooks';
import { setSidebar, setToast } from '../../../../app/appSlice';
import "./DataLibrary.scss";
import { IconMode } from '../../../../components/Mode/icon';
import { RuntimeMode } from '../../../../app/apiSlice';
import ImportFunction from "./ImportFunction";
import { DisplayLabels } from '../../../../components/labels/DisplayLabels';
import DeleteDataLibraryItem from './DeleteDataLibraryItem';
import { useSecureApplication } from "../../../../hooks/KeycloackConnection";
import { Panel } from 'primereact/panel';
import { Button } from 'primereact/button';
import useHandleExport, { ExportExtension } from '../../../../hooks/handleExport';
import { useLazyYamlFunctionQuery } from '../../DataLibrarySlice';
import { LabelsSelectorsProvider, SelectedLabels } from '@kosmos/shared';

interface Props {
    id: string;
    name: string;
    description: string;
    runtimeMode?: RuntimeMode;
    metadatas?: SelectedLabels;
    translatedMetadatas?: SelectedLabels;
    author: string | undefined;
    onRefreshCatalog?: () => void
}

export const DataLibraryFunctionItem =
    ({id, name, description, runtimeMode, metadatas, translatedMetadatas, author, onRefreshCatalog}: Props) => {

    const dispatch = useAppDispatch();
    const runtimes = useAppSelector((state) => state.app.config.runtimeMode);
    const ref = useRef<Panel>(null);
    const {hasAnyPerm} = useSecureApplication();
    const { exportFile } = useHandleExport();
    const [getYamlUDF] = useLazyYamlFunctionQuery();

    const handleExportUDF = async () => {
        try {
            const yaml = await getYamlUDF({id: id}).unwrap();
            exportFile(yaml, name, ExportExtension.YAML);
        } catch (e) {
            dispatch(
                setToast({
                    severity: 'error',
                    title: 'Erreur',
                    content: `Echec du téléchargement de la brique ${name}`,
                    life: 3000,
                })
            );
        }

    }

    const headerTemplate = (options: { className: any, togglerElement: any }) => {
        const className = `${options.className} flex`;
        const isExpanded = options.togglerElement.props['aria-expanded'];

        return (
            <div className={className}>
                <div className="flex flex-1 flex-column gap-2 cursor-pointer tooltip-description"
                     onClick={e => ref?.current?.toggle(e)}>
                    <div className={'flex align-items-center gap-2'}>
                        <DisplayItemIcon/> &nbsp;{name}&nbsp; <span
                        style={{color: '#54769a', fontSize: '12px'}}>({author || 'Athea'})</span>
                    </div>
                    {metadatas && <div className={'flex mb-2 metadatas'}>
                        Labels : <DisplayLabels metadatas={translatedMetadatas}/>
                    </div>}
                </div>
                <>
                    <Button onClick={e => ref?.current?.toggle(e)}
                        title={!isExpanded ? 'Voir la description' : 'Masquer la description'}
                        icon={!isExpanded ? 'pi pi-angle-double-down' : 'pi pi-angle-double-up'}
                        className="p-button-text" />
                    {hasAnyPerm('functions.all', 'functions.upd') && <Button
                        title={'Editer'}
                        onClick={() =>
                            dispatch(
                                setSidebar({
                                    title: `Modifier : ${name}`,
                                    content: (
                                        <LabelsSelectorsProvider serviceUrl="/api/labels?vstoreobject=udf">
                                            <ImportFunction onUploadDone={() => {
                                                if (onRefreshCatalog)
                                                    onRefreshCatalog()
                                            }} functionName={name} metadatas={metadatas}/>
                                        </LabelsSelectorsProvider>
                                    ),
                                })
                            )
                        }
                        icon="pi pi-pencil"
                        className="p-button-text"
                    />}
                    <Button
                        title={'Télécharger'}
                        onClick={() => handleExportUDF()}
                        icon="pi pi-download"
                        className="p-button-text"
                    />
                    {hasAnyPerm('functions.all', 'functions.del') &&
                        <DeleteDataLibraryItem itemName={name} itemID={id}
                            onDeleteDone={() => {
                                if (onRefreshCatalog) {
                                    onRefreshCatalog()
                                }
                            }}
                        />}
                </>
            </div>
        );
    };

    const DisplayItemIcon = () => {
        const runtime = runtimes.find(r => r.name === runtimeMode);
        return runtime ? <IconMode name={runtime.displayName} color={runtime.icon.color} fontSize="1.5rem" type={runtime.icon.type}></IconMode> : <></>;
    }

    return (
        <Panel headerTemplate={headerTemplate} ref={ref} collapsed={true} toggleable>
            <span className={"description w-full"}>{description}</span>
        </Panel>
    );
};
